import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

import { GasWalletsTable } from './GasWalletsTable';
import type { GasWallet } from './types';

type Props = {
  wallets: GasWallet[] | undefined;
  isLoading: boolean;
  /** Why the read failed, when it did. */
  errorMessage: string | null;
};

/**
 * The platform gas wallets: one address per chain family (EVM, Solana, Tron), read live on every chain
 * that accepts tokens. They pay the gas of every token sweep; an empty one stops token payouts on its
 * chain, so a low balance is flagged for a top-up. The testnets (sandbox stores) come in their own
 * group, low only when empty, and are left out of the low count in the title.
 */
export function GasWalletsPanel({ wallets, isLoading, errorMessage }: Props) {
  const { t } = useTranslation('manageCryptoPayments');
  const mainnets = (wallets ?? []).filter((wallet) => !wallet.testnet);
  const testnets = (wallets ?? []).filter((wallet) => wallet.testnet);
  const lowCount = mainnets.filter((wallet) => wallet.lowBalance).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {t('gasWallets.title')}
          {lowCount > 0 && <Badge variant="yellow">{t('gasWallets.lowCount', { count: lowCount })}</Badge>}
        </CardTitle>
        <CardDescription>{t('gasWallets.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex h-24 items-center justify-center">
            <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
          </div>
        ) : errorMessage ? (
          <p className="text-destructive flex items-center gap-2 text-sm">
            <AlertTriangle className="h-4 w-4" />
            {t('gasWallets.loadFailed', { error: errorMessage })}
          </p>
        ) : (
          <div className="space-y-6">
            <GasWalletsTable wallets={mainnets} />
            {testnets.length > 0 && (
              <section className="space-y-2">
                <h3 className="text-sm font-medium">{t('gasWallets.testnetsTitle')}</h3>
                <p className="text-muted-foreground text-xs">{t('gasWallets.testnetsDescription')}</p>
                <GasWalletsTable wallets={testnets} />
              </section>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
